import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import { createApiClient } from "@/config/createApiClient";
import { selectedOrganisationIdAtom } from "@/store";

import { AddressApi } from "../../config/apiClient";

const apiClient = createApiClient(AddressApi);

export const useAddress = () => {
  const queryClient = useQueryClient();
  const selectedOrganisationIdAtomValue = useAtomValue(
    selectedOrganisationIdAtom
  );

  function useGetAddress() {
    return useQuery({
      queryKey: ["addresses", selectedOrganisationIdAtomValue],
      enabled: selectedOrganisationIdAtomValue > 0,
      queryFn: () => {
        return apiClient.addressesControllerGetAddresses({
          organisationId: selectedOrganisationIdAtomValue,
        });
      },
    });
  }

  function useAddAddress() {
    return useMutation({
      mutationKey: ["addAddress"],
      mutationFn: (
        body: Parameters<
          typeof apiClient.addressesControllerCreateAddress
        >["0"]["addressCreateCommand"]
      ) =>
        apiClient.addressesControllerCreateAddress({
          organisationId: selectedOrganisationIdAtomValue,
          addressCreateCommand: body,
        }),
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["addresses"] }),
          queryClient.invalidateQueries({
            queryKey: ["getUserDefaultAddress"],
          }),
        ]);
      },
    });
  }

  function useUpdateAddress() {
    return useMutation({
      mutationKey: ["updateAddress"],
      mutationFn: (
        body: Parameters<
          typeof apiClient.addressesControllerUpdateAddress
        >["0"]["addressUpdateCommand"]
      ) => {
        return apiClient.addressesControllerUpdateAddress({
          addressId: body.id,
          organisationId: selectedOrganisationIdAtomValue.toString(),
          addressUpdateCommand: body,
        });
      },
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["addresses"] }),
          queryClient.invalidateQueries({
            queryKey: ["getUserDefaultAddress"],
          }),
        ]);
      },
    });
  }

  function useDeleteAddress() {
    return useMutation({
      mutationKey: ["deleteAddress"],
      mutationFn: (
        addressDeleteCommand: Parameters<
          typeof apiClient.addressesControllerDeleteAddress
        >["0"]["addressDeleteCommand"]
      ) =>
        apiClient.addressesControllerDeleteAddress({
          organisationId: selectedOrganisationIdAtomValue,
          addressId: addressDeleteCommand.id,
          addressDeleteCommand,
        }),
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["addresses"] }),
          queryClient.invalidateQueries({
            queryKey: ["getUserDefaultAddress"],
          }),
        ]);
      },
    });
  }

  return {
    useGetAddress,
    useAddAddress,
    useUpdateAddress,
    useDeleteAddress,
  };
};
